<template>
  <div class="menu-page vision-analysis-page vision-scope">
    <header class="vision-hero">
      <div class="vision-hero__text">
        <h1>Leitura por IA</h1>
        <p class="vision-hero__subtitle">
          Processamento por imagem integrado ao fluxo autenticado da plataforma.
        </p>
      </div>

      <transition name="vision-fade">
        <div v-if="contextChips.length" class="vision-hero__context" :key="contextChipsKey">
          <span
            v-for="chip in contextChips"
            :key="chip.key"
            :class="['vision-chip', chip.tone ? `vision-chip--${chip.tone}` : '']"
          >
            <v-icon size="14">{{ chip.icon }}</v-icon>
            <span>{{ chip.label }}</span>
          </span>
        </div>
      </transition>
    </header>

    <nav v-if="step !== 4" class="vision-stepper" :aria-label="'Etapas da análise'">
      <ol class="vision-stepper__list">
        <li
          v-for="item in stepperItems"
          :key="item.id"
          :class="[
            'vision-stepper__item',
            { 'is-active': step === item.id, 'is-complete': step > item.id, 'is-clickable': item.clickable },
          ]"
          :aria-current="step === item.id ? 'step' : undefined"
          @click="item.clickable && goToStep(item.id)"
        >
          <div class="vision-stepper__bullet">
            <v-icon v-if="step > item.id" size="16" color="white">mdi-check</v-icon>
            <span v-else>{{ item.id }}</span>
          </div>
          <div class="vision-stepper__meta">
            <span class="vision-stepper__eyebrow">Passo {{ item.id }}</span>
            <span class="vision-stepper__label">{{ item.label }}</span>
            <span v-if="item.summary" class="vision-stepper__summary">{{ item.summary }}</span>
          </div>
        </li>
      </ol>
    </nav>

    <transition name="vision-step" mode="out-in">
      <section :key="step" class="vision-card vision-step-card">
        <!-- Passo 1: Tipo de teste -->
        <div v-if="step === 1" class="vision-step vision-step--1">
          <div class="vision-step__intro">
            <div>
              <h2>Selecione o teste cadastrado</h2>
              <p>Escolha um teste já configurado para iniciar a análise. As configurações de leitura serão aplicadas automaticamente.</p>
            </div>
            <div class="vision-info-card">
              <v-icon color="#4676D4" size="20">mdi-information-outline</v-icon>
              <div>
                <strong>Dica</strong>
                <span>Após selecionar, a câmera abrirá no modo de detecção automática com IA.</span>
              </div>
            </div>
          </div>

          <component
            :is="stripTestFieldComponent"
            v-model="selectedStripTest"
            :items="stripTests"
            :item-text="getStripTestLabel"
            return-object
            outlined
            clearable
            prepend-inner-icon="mdi-test-tube"
            label="Teste cadastrado"
            :loading="stripTestsLoading"
            :disabled="loading || stripTestsLoading"
            no-data-text="Nenhum teste cadastrado"
            class="vision-input"
            hide-details="auto"
          ></component>

          <v-alert v-if="selectedStripTest && intervalValidation.message" dense text type="warning" class="vision-alert mt-3">
            {{ intervalValidation.message }}
          </v-alert>

          <div v-if="canStartCapture" class="vision-step__actions">
            <v-btn
              color="#4676D4"
              dark
              large
              class="vision-btn-primary"
              @click="startCapture"
            >
              <v-icon left>mdi-image-search-outline</v-icon>
              Iniciar leitura com IA
            </v-btn>
          </div>

          <transition name="vision-fade">
            <div v-if="loading" class="vision-loading">
              <div class="vision-loading__spinner" aria-hidden="true"></div>
              <div class="vision-loading__stages">
                <p class="vision-loading__title">{{ loadingText || "Processando..." }}</p>
                <ol class="vision-loading__steps">
                  <li
                    v-for="(stage, idx) in loadingStages"
                    :key="idx"
                    :class="{
                      'is-done': idx < loadingStageIndex,
                      'is-active': idx === loadingStageIndex,
                    }"
                  >
                    <v-icon v-if="idx < loadingStageIndex" size="14" color="#198754">mdi-check-circle</v-icon>
                    <v-icon v-else-if="idx === loadingStageIndex" size="14" color="#4676D4">mdi-progress-clock</v-icon>
                    <v-icon v-else size="14" color="#9ca3af">mdi-circle-outline</v-icon>
                    {{ stage }}
                  </li>
                </ol>
              </div>
            </div>
          </transition>
        </div>

        <!-- Passo 2: Resultado -->
        <div v-else-if="step === 2" class="vision-step vision-step--3">
          <div v-if="result">
            <div
              class="vision-result-hero"
              :style="{
                '--result-color': resultMeta(result).color,
                '--result-text': resultMeta(result).textColor,
              }"
            >
              <div class="vision-result-hero__bar"></div>
              <div class="vision-result-hero__icon">
                <v-icon size="44" :color="resultMeta(result).color">{{ resultMeta(result).icon }}</v-icon>
              </div>
              <div class="vision-result-hero__body">
                <span class="vision-eyebrow">Resultado final</span>
                <h2>{{ resultMeta(result).label }}</h2>
                <p class="vision-result-hero__meta">
                  <span><v-icon size="16">mdi-test-tube</v-icon>{{ result.testType || "Leitura por IA" }}</span>
                  <span><v-icon size="16">mdi-clock-outline</v-icon>{{ analyzedAt }}</span>
                  <span><v-icon size="16">mdi-image-search-outline</v-icon>Análise com IA</span>
                </p>
              </div>
            </div>

            <v-alert
              v-if="hasDifficultImageWarning"
              text
              type="warning"
              icon="mdi-camera-alert-outline"
              class="vision-image-warning"
            >
              <div class="vision-image-warning__content">
                <strong>Resultado com menor confiabilidade visual</strong>
                <span>As leituras RGB e de luminância divergiram ou não tiveram nitidez suficiente. Se possível, tire uma nova foto em um ambiente bem iluminado.</span>
              </div>
            </v-alert>

            <div class="vision-result-image">
              <div class="vision-detection-alert">
                <v-icon size="16" color="#b45309">mdi-alert-outline</v-icon>
                <span>Verifique se a área das linhas foi detectada corretamente nesta imagem.</span>
              </div>
              <VisionChartCanvas :result="result" :intervals="intervals" mode="image" />
            </div>

            <v-expansion-panels flat class="vision-result-charts">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span><v-icon left small color="#4676D4">mdi-format-list-bulleted</v-icon>Leitura detalhada por RGB e Luminância</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <VisionDiagnosisSummary :result="result" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <v-expansion-panels flat class="vision-result-charts">
              <v-expansion-panel>
                <v-expansion-panel-header>
                  <span><v-icon left small color="#4676D4">mdi-chart-line-variant</v-icon>Gráficos avançados (RGB e luminância)</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <VisionChartCanvas :result="result" :intervals="intervals" mode="charts" />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <div class="vision-step__actions vision-step__actions--split">
              <v-btn outlined color="#4676D4" :disabled="loading" class="vision-btn-outline" @click="retakePhoto">
                <v-icon left>mdi-camera-retake-outline</v-icon>
                Refazer leitura
              </v-btn>
              <v-btn color="#198754" dark large :disabled="loading" class="vision-btn-primary" @click="acceptResult">
                Aceitar resultado
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </div>
          </div>

          <div v-else class="vision-empty">
            <v-icon size="48" color="#9ca3af">mdi-image-off-outline</v-icon>
            <h3>Nenhum resultado disponível</h3>
            <p>Capture a imagem da tira de teste para visualizar o resultado.</p>
            <v-btn outlined color="#4676D4" @click="startCapture">
              <v-icon left>mdi-camera-outline</v-icon>
              Capturar imagem
            </v-btn>
          </div>
        </div>

        <!-- Passo 3: Salvar -->
        <div v-else-if="step === 3" class="vision-step vision-step--4">
          <div class="vision-step__intro">
            <div>
              <h2>Salvar análise</h2>
              <p>Confirme as informações para registrar este teste no histórico.</p>
            </div>
          </div>

          <div class="vision-save-grid">
            <div class="vision-save-form">
              <fieldset class="vision-fieldset">
                <legend class="vision-eyebrow">Identificação</legend>
                <v-text-field
                  v-model="saveForm.name"
                  label="Nome do teste *"
                  :rules="requiredRules"
                  outlined
                  prepend-inner-icon="mdi-tag-outline"
                  hide-details="auto"
                  class="vision-input"
                ></v-text-field>
              </fieldset>

              <fieldset class="vision-fieldset">
                <legend class="vision-eyebrow">Origem & responsável</legend>
                <v-text-field
                  v-model="saveForm.origem"
                  label="Origem *"
                  :rules="requiredRules"
                  outlined
                  prepend-inner-icon="mdi-map-marker-outline"
                  hide-details="auto"
                  class="vision-input"
                ></v-text-field>
                <v-text-field
                  v-model="saveForm.analista"
                  label="Analista"
                  outlined
                  prepend-inner-icon="mdi-account-outline"
                  hide-details="auto"
                  class="vision-input"
                ></v-text-field>
              </fieldset>

              <fieldset class="vision-fieldset">
                <legend class="vision-eyebrow">Adicional</legend>
                <v-text-field
                  v-model="saveForm.volume"
                  label="Volume (opcional)"
                  outlined
                  type="number"
                  prepend-inner-icon="mdi-beaker-outline"
                  hide-details="auto"
                  class="vision-input"
                ></v-text-field>

                <v-text-field
                  v-model="saveForm.temperatura"
                  label="Temperatura do leite (opcional)"
                  suffix="ºC"
                  outlined
                  type="number"
                  prepend-inner-icon="mdi-thermometer"
                  hide-details="auto"
                  class="vision-input"
                ></v-text-field>
              </fieldset>
            </div>

            <aside v-if="result" class="vision-save-preview">
              <div class="vision-save-preview__sticky">
                <span class="vision-eyebrow">Pré-visualização</span>

                <div
                  class="vision-save-preview__result"
                  :style="{ '--result-color': resultMeta(result).color }"
                >
                  <v-icon :color="resultMeta(result).color" size="20">{{ resultMeta(result).icon }}</v-icon>
                  <span>{{ resultMeta(result).label }}</span>
                </div>

                <div v-if="imagePreviewSrc" class="vision-save-preview__image">
                  <img :src="imagePreviewSrc" alt="Imagem que será salva no histórico" />
                </div>

                <ul class="vision-save-preview__meta">
                  <li><v-icon size="16" color="#6b7280">mdi-test-tube</v-icon>{{ result.testType || "Leitura por IA" }}</li>
                  <li><v-icon size="16" color="#6b7280">mdi-image-search-outline</v-icon>Detecção com IA</li>
                  <li><v-icon size="16" color="#6b7280">mdi-clock-outline</v-icon>{{ analyzedAt }}</li>
                </ul>
              </div>
            </aside>
          </div>

          <transition name="vision-fade">
            <div v-if="saving" class="vision-loading">
              <div class="vision-loading__spinner" aria-hidden="true"></div>
              <div class="vision-loading__stages">
                <p class="vision-loading__title">Salvando teste...</p>
                <p class="vision-loading__subtitle">Estamos enviando a imagem e gravando o resultado.</p>
              </div>
            </div>
          </transition>

          <p v-if="!saving && missingSaveFields.length" class="vision-save-required-hint">
            <v-icon size="15" color="#b45309">mdi-alert-circle-outline</v-icon>
            <span>Preencha {{ missingSaveFields.join(" e ") }} para salvar o teste.</span>
          </p>

          <div class="vision-step__actions vision-step__actions--split">
            <v-btn text :disabled="saving" class="vision-btn-text" @click="goToStep(2)">
              <v-icon left>mdi-arrow-left</v-icon>
              Voltar
            </v-btn>
            <v-btn color="#198754" dark large :loading="saving" :disabled="!canSave" class="vision-btn-primary" @click="saveTest">
              <v-icon left>mdi-content-save-outline</v-icon>
              Salvar teste
            </v-btn>
          </div>
        </div>

        <!-- Passo 4: Sucesso -->
        <div v-else-if="step === 4" class="vision-step vision-step--success">
          <div class="vision-success">
            <div class="vision-success__check">
              <v-icon size="64" color="white">mdi-check</v-icon>
            </div>
            <h2>Teste salvo com sucesso</h2>
            <p>
              <strong>{{ lastSavedTestName || "Sua análise" }}</strong>
              foi registrada no histórico.
            </p>

            <div class="vision-success__actions">
              <v-btn outlined color="#4676D4" large class="vision-btn-outline" @click="goToTestsHistory">
                <v-icon left>mdi-history</v-icon>
                Ver histórico
              </v-btn>
              <v-btn color="#4676D4" dark large class="vision-btn-primary" @click="resetWizard">
                <v-icon left>mdi-plus</v-icon>
                Nova análise
              </v-btn>
            </div>
          </div>
        </div>
      </section>
    </transition>

    <VisionCameraCapture v-model="cameraDialog" :mode="cameraMode" @captured="handleCameraCaptured" @error="handleCameraError" />
  </div>
</template>

<script>
import Api from "@/lib/Api";
import { emitToastr } from "@/lib/Utils";
import { VAutocomplete, VSelect } from "vuetify/lib";
import VisionCameraCapture from "./VisionAnalysis/VisionCameraCapture.vue";
import VisionChartCanvas from "./VisionAnalysis/VisionChartCanvas.vue";
import VisionDiagnosisSummary from "./VisionAnalysis/VisionDiagnosisSummary.vue";
import {
  MAX_IMAGE_FILES,
  getAiResultFiles,
  getResultMeta,
  isAiCompatibleFile,
  normalizeVisionResult,
  sanitizeIntervals,
  stripTestToIntervals,
  validateIntervals,
} from "./VisionAnalysis/visionUtils";

const STEP_DEFINITIONS = [
  { id: 1, label: "Tipo de teste" },
  { id: 2, label: "Modo de leitura" },
  { id: 3, label: "Resultado" },
  { id: 4, label: "Salvar" },
];

const AI_LOADING_STAGES = [
  "Enviando imagem para a IA...",
  "Detectando estojo e tira na imagem...",
  "Calculando intensidades RGB e luminância...",
];

function pad(value) {
  return String(value).padStart(2, "0");
}

function defaultTestName(testName) {
  const now = new Date();
  const date = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()}`;
  const time = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return `${testName || "Leitura por IA"} - ${date} ${time}`;
}

export default {
  name: "VisionAnalysisView",

  components: {
    VAutocomplete,
    VSelect,
    VisionCameraCapture,
    VisionChartCanvas,
    VisionDiagnosisSummary,
  },

  data() {
    return {
      step: 1,
      stripTests: [],
      selectedStripTest: null,
      stripTestsLoading: false,
      intervals: [],
      cameraDialog: false,
      cameraMode: "ai",
      result: null,
      analyzedAt: "",
      loading: false,
      loadingText: "",
      loadingStages: [],
      loadingStageIndex: 0,
      loadingTimer: null,
      saving: false,
      saveForm: { name: "", origem: "", analista: "", volume: "", temperatura: "" },
      requiredRules: [(v) => !!v || "Campo obrigatório"],
      lastSavedTestName: "",
    };
  },

  computed: {
    stripTestFieldComponent() {
      return this.$vuetify.breakpoint.smAndDown ? "v-select" : "v-autocomplete";
    },

    intervalValidation() {
      if (!this.selectedStripTest) {
        return { valid: false, message: "" };
      }
      return validateIntervals(this.intervals);
    },

    canSave() {
      return Boolean(this.result && !this.saving && this.saveForm.name && this.saveForm.origem);
    },

    hasDifficultImageWarning() {
      if (!this.result) return false;

      const rgbResult = String(this.result.diagRgb && this.result.diagRgb.result || "").toLowerCase();
      const luminanceResult = String(this.result.diagLum && this.result.diagLum.result || "").toLowerCase();
      const matchingConclusiveResult = rgbResult === luminanceResult && ["positivo", "negativo"].includes(rgbResult);

      return !matchingConclusiveResult;
    },

    missingSaveFields() {
      const missing = [];
      if (!this.saveForm.name) missing.push("Nome do teste");
      if (!this.saveForm.origem) missing.push("Origem");
      return missing;
    },

    canStartCapture() {
      return Boolean(this.selectedStripTest && this.intervalValidation.valid && !this.loading);
    },

    imagePreviewSrc() {
      if (!this.result) return "";
      if (this.result.originalImageFile) return URL.createObjectURL(this.result.originalImageFile);
      if (this.result.imageDataUri) return this.result.imageDataUri;
      if (this.result.imageFile) return URL.createObjectURL(this.result.imageFile);
      return "";
    },

    stepperItems() {
      return STEP_DEFINITIONS.map((definition) => {
        let summary = "";
        if (definition.id === 1 && this.selectedStripTest) {
          summary = this.getStripTestLabel(this.selectedStripTest);
        } else if (definition.id === 2 && this.result) {
          summary = getResultMeta(this.result.finalResult).label;
        }

        const clickable = this.step !== 4 && (
          (definition.id === 1) ||
          (definition.id === 2 && Boolean(this.result)) ||
          (definition.id === 3 && Boolean(this.result))
        );

        return {
          ...definition,
          summary,
          clickable,
        };
      });
    },

    contextChips() {
      const chips = [];

      if (this.selectedStripTest) {
        chips.push({
          key: "test",
          icon: "mdi-test-tube",
          label: this.getStripTestLabel(this.selectedStripTest),
          tone: "neutral",
        });
      }

      if (this.selectedStripTest) {
        chips.push({
          key: "mode",
          icon: "mdi-image-search-outline",
          label: "IA",
          tone: "primary",
        });
      }

      if (this.step !== 4) {
        chips.push({
          key: "step",
          icon: "mdi-progress-clock",
          label: `Etapa ${this.step} de 3`,
          tone: "soft",
        });
      }

      return chips;
    },

    contextChipsKey() {
      return this.contextChips.map((chip) => `${chip.key}:${chip.label}`).join("|");
    },
  },

  watch: {
    selectedStripTest(next, previous) {
      this.intervals = next ? stripTestToIntervals(next) : [];

      if (previous && (!next || String(previous._id) !== String(next._id))) {
        this.result = null;
      }
    },
  },

  beforeDestroy() {
    this.stopLoadingStages();
  },

  async mounted() {
    await this.loadStripTests();
  },

  methods: {
    getStripTestLabel(test) {
      if (!test) return "";
      const code = test.code ? ` - ${test.code}` : "";
      return `${test.name}${code}`;
    },

    async loadStripTests() {
      this.stripTestsLoading = true;

      try {
        const resp = await Api.getStripTests();

        if (resp && resp.message) {
          if (resp.error) {
            emitToastr("error", resp.message || "Erro ao carregar testes cadastrados.", "Erro");
          } else {
            this.stripTests = resp.message;
          }
        } else {
          emitToastr("error", "Não foi possível carregar os testes cadastrados.", "Erro");
        }
      } catch (err) {
        emitToastr("error", err && err.message ? err.message : "Não foi possível carregar os testes cadastrados.", "Erro");
      } finally {
        this.stripTestsLoading = false;
      }
    },

    goToStep(n) {
      this.step = n;
    },

    goToTestsHistory() {
      try {
        this.$router.push({ name: "Testes" });
      } catch (err) {
        try {
          this.$router.push("/testes");
        } catch (innerErr) {
          this.resetWizard();
        }
      }
    },

    startCapture() {
      if (!this.canStartCapture) return;
      this.cameraMode = "ai";
      this.cameraDialog = true;
    },

    handleCameraError(message) {
      if (message) emitToastr("error", message, "Erro");
    },

    async handleCameraCaptured(payload) {
      const file = payload.file;
      const originalFile = payload.originalFile || payload.file;

      await this.runAiAnalysis(file, originalFile);

      if (this.result) {
        this.goToStep(2);
      }
    },

    retakePhoto() {
      this.result = null;
      this.cameraMode = "ai";
      this.cameraDialog = true;
    },

    acceptResult() {
      if (!this.result) return;
      const baseName = (this.selectedStripTest && this.selectedStripTest.name) || this.result.testType;
      this.saveForm = {
        name: defaultTestName(baseName),
        origem: "",
        analista: "",
        volume: "",
        temperatura: "",
      };
      this.goToStep(3);
    },

    resultMeta(result) {
      return getResultMeta(result.finalResult);
    },

    formatNow() {
      const now = new Date();
      return `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    },

    startLoadingStages(stages) {
      this.stopLoadingStages();
      this.loadingStages = stages.slice();
      this.loadingStageIndex = 0;
      this.loadingText = stages[0] || "";

      const advance = () => {
        if (this.loadingStageIndex >= this.loadingStages.length - 1) return;
        this.loadingStageIndex += 1;
        this.loadingText = this.loadingStages[this.loadingStageIndex] || this.loadingText;
        this.loadingTimer = setTimeout(advance, 1400);
      };
      this.loadingTimer = setTimeout(advance, 1100);
    },

    stopLoadingStages() {
      if (this.loadingTimer) {
        clearTimeout(this.loadingTimer);
        this.loadingTimer = null;
      }
    },

    setLoadingStage(index) {
      const target = Math.max(0, Math.min(index, this.loadingStages.length - 1));
      this.loadingStageIndex = target;
      this.loadingText = this.loadingStages[target] || this.loadingText;
    },

    validateBeforeAnalysis(file, mode) {
      if (!this.selectedStripTest) {
        return "Selecione o teste cadastrado antes da análise.";
      }
      if (!file) {
        return "Capture uma imagem para análise.";
      }
      if (!this.intervalValidation.valid) {
        return this.intervalValidation.message;
      }
      if (mode === "ai" && !isAiCompatibleFile(file)) {
        return "A análise com IA aceita somente JPG, JPEG ou PNG.";
      }
      if (MAX_IMAGE_FILES < 1) {
        return "Limite de imagens inválido.";
      }
      return "";
    },

    setLocalError(message) {
      if (message) emitToastr("error", message, "Erro");
    },

    setSingleResult(apiResult, sourceFile, originalFile) {
      const intervals = sanitizeIntervals(this.intervals);
      const selectedTest = this.selectedStripTest || {};

      this.result = {
        ...normalizeVisionResult(apiResult, sourceFile, intervals, originalFile),
        testId: selectedTest.id,
        testType: selectedTest.name || "Leitura por IA",
        categoryId: selectedTest.categoryId,
      };

      this.analyzedAt = this.formatNow();
    },

    async runAiAnalysis(file, originalFile) {
      const validationMessage = this.validateBeforeAnalysis(file, "ai");
      if (validationMessage) {
        this.setLocalError(validationMessage);
        return;
      }

      this.loading = true;
      this.startLoadingStages(AI_LOADING_STAGES);

      try {
        const payload = await Api.visionAnalyzeAi([file]);
        const { files: aiFiles } = getAiResultFiles(payload && payload.results);

        if (!aiFiles.length) {
          const aiResult = payload && Array.isArray(payload.results) ? payload.results[0] : null;
          throw new Error((aiResult && aiResult.error) || "A análise com IA não retornou imagem válida.");
        }

        const aiFile = aiFiles[0];
        this.setLoadingStage(2);

        const results = await Api.visionAnalyze(
          [aiFile],
          sanitizeIntervals(this.intervals),
          this.selectedStripTest.id
        );
        if (!Array.isArray(results) || !results.length) {
          throw new Error("A Vision API retornou um formato inesperado.");
        }
        this.setSingleResult(results[0], aiFile, originalFile || file);
        this.setLoadingStage(this.loadingStages.length - 1);
      } catch (err) {
        emitToastr("error", err && err.message ? err.message : "Erro na análise com IA.", "Erro");
        this.result = null;
      } finally {
        this.stopLoadingStages();
        this.loading = false;
        this.loadingText = "";
        this.loadingStages = [];
        this.loadingStageIndex = 0;
      }
    },

    async saveTest() {
      if (!this.result || !this.canSave) return;

      this.saving = true;

      try {
        let imageUrl = null;

        const imageToUpload = this.result.originalImageFile || this.result.imageFile;
        if (imageToUpload) {
          const uploadResp = await Api.uploadTestImage(imageToUpload);
          if (!uploadResp || uploadResp.error || !uploadResp.message) {
            throw new Error((uploadResp && uploadResp.message) || "Erro ao enviar imagem.");
          }
          imageUrl = uploadResp.message;
        }

        const selectedTest = this.selectedStripTest || {};
        const extras = {
          origem: this.saveForm.origem,
          analista: this.saveForm.analista,
        };
        if (this.saveForm.volume !== "" && this.saveForm.volume !== null && this.saveForm.volume !== undefined) {
          extras.volume = this.saveForm.volume;
        }
        // Enviada como número para o backend não aplicar o toUpperCase de extras string.
        if (this.saveForm.temperatura !== "" && this.saveForm.temperatura !== null && this.saveForm.temperatura !== undefined && !isNaN(this.saveForm.temperatura)) {
          extras.temperatura = Number(this.saveForm.temperatura);
        }

        const payload = {
          name: this.saveForm.name,
          readingType: "IA",
          finalResult: this.result.finalResult,
          testType: selectedTest.name || this.result.testType,
          testId: selectedTest.id !== undefined && selectedTest.id !== null ? String(selectedTest.id) : undefined,
          categoryId: selectedTest.categoryId !== undefined && selectedTest.categoryId !== null ? String(selectedTest.categoryId) : undefined,
          mapResult: this.result.mapResult,
          imageUrl,
          extras,
        };

        const resp = await Api.createTest(payload);

        if (!resp || resp.error) {
          throw new Error((resp && resp.message) || "Erro ao salvar teste.");
        }

        emitToastr("success", "Teste salvo com sucesso.", "Sucesso");
        this.lastSavedTestName = this.saveForm.name;
        this.goToStep(4);
      } catch (err) {
        emitToastr("error", err && err.message ? err.message : "Erro ao salvar teste.", "Erro");
      } finally {
        this.saving = false;
      }
    },

    resetWizard() {
      this.result = null;
      this.selectedStripTest = null;
      this.intervals = [];
      this.saveForm = { name: "", origem: "", analista: "", volume: "", temperatura: "" };
      this.lastSavedTestName = "";
      this.analyzedAt = "";
      this.step = 1;
    },
  },
};
</script>

<style>
@import "./VisionAnalysis/visionDesignTokens.css";
</style>

<style scoped>
.vision-analysis-page {
  padding: 0 0 64px;
}

/* ===== Header ===== */
.vision-hero {
  display: flex;
  flex-direction: column;
  gap: var(--vision-space-4);
  padding: var(--vision-space-6) var(--vision-space-5) var(--vision-space-5);
  margin-bottom: var(--vision-space-5);
  background: linear-gradient(135deg, #ffffff 0%, var(--vision-surface-2) 100%);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-lg);
  box-shadow: var(--vision-shadow-1);
}

.vision-hero__text h1 {
  margin: 4px 0 6px;
  color: var(--vision-ink-1);
  font-size: 30px;
  line-height: 1.15;
  letter-spacing: -0.4px;
  font-weight: 700;
}

.vision-hero__subtitle {
  margin: 0;
  color: var(--vision-ink-5);
  font-size: 15px;
  max-width: 720px;
}

.vision-hero__context {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.vision-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 12px;
  border-radius: var(--vision-radius-pill);
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  color: var(--vision-ink-3);
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.1px;
}

.vision-chip--primary {
  background: var(--vision-primary-soft);
  border-color: rgba(70, 118, 212, 0.25);
  color: var(--vision-primary-dark);
}

.vision-chip--soft {
  background: var(--vision-surface-3);
  border-color: transparent;
  color: var(--vision-ink-4);
}

/* ===== Stepper ===== */
.vision-stepper {
  margin-bottom: var(--vision-space-5);
}

.vision-stepper__list {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-lg);
  box-shadow: var(--vision-shadow-1);
  overflow: hidden;
}

.vision-stepper__item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  position: relative;
  transition: background var(--vision-anim-fast) var(--vision-ease);
}

.vision-stepper__item + .vision-stepper__item {
  border-left: 1px solid var(--vision-border);
}

.vision-stepper__item.is-clickable {
  cursor: pointer;
}

.vision-stepper__item.is-clickable:hover {
  background: var(--vision-primary-softer);
}

.vision-stepper__bullet {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vision-surface-3);
  color: var(--vision-ink-5);
  font-size: 14px;
  font-weight: 700;
  border: 2px solid transparent;
  transition: all var(--vision-anim-base) var(--vision-ease);
}

.vision-stepper__item.is-active .vision-stepper__bullet {
  background: var(--vision-primary);
  color: #fff;
  box-shadow: var(--vision-shadow-primary-soft);
}

.vision-stepper__item.is-complete .vision-stepper__bullet {
  background: var(--vision-success);
  color: #fff;
}

.vision-stepper__meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.vision-stepper__eyebrow {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--vision-ink-6);
}

.vision-stepper__item.is-active .vision-stepper__eyebrow {
  color: var(--vision-primary);
}

.vision-stepper__label {
  font-size: 14px;
  font-weight: 600;
  color: var(--vision-ink-2);
  line-height: 1.25;
}

.vision-stepper__summary {
  font-size: 12.5px;
  color: var(--vision-ink-5);
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ===== Step card ===== */
.vision-card {
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-lg);
  box-shadow: var(--vision-shadow-2);
}

.vision-step-card {
  padding: var(--vision-space-6);
}

.vision-step__intro {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--vision-space-5);
  margin-bottom: var(--vision-space-5);
  flex-wrap: wrap;
}

.vision-step__intro h2 {
  margin: 0 0 4px;
  font-size: 20px;
  color: var(--vision-ink-1);
  font-weight: 700;
  letter-spacing: -0.2px;
}

.vision-step__intro p {
  margin: 0;
  color: var(--vision-ink-5);
  font-size: 14px;
  max-width: 540px;
}

.vision-info-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: var(--vision-primary-softer);
  border: 1px solid rgba(70, 118, 212, 0.18);
  border-radius: var(--vision-radius-md);
  max-width: 360px;
  font-size: 13px;
  color: var(--vision-ink-3);
}

.vision-info-card strong {
  display: block;
  color: var(--vision-primary-dark);
  font-size: 12.5px;
  margin-bottom: 2px;
}

.vision-input ::v-deep .v-input__slot {
  border-radius: var(--vision-radius-md) !important;
}

.vision-alert {
  border-radius: var(--vision-radius-md) !important;
}

.vision-image-warning {
  margin: 0 0 var(--vision-space-4) !important;
  border: 1px solid rgba(245, 159, 0, 0.38) !important;
  border-radius: var(--vision-radius-md) !important;
  background: linear-gradient(135deg, #fffaf0 0%, #fff7df 100%) !important;
}

.vision-image-warning__content {
  display: flex;
  flex-direction: column;
  gap: 3px;
  color: #78350f;
  font-size: 13.5px;
  line-height: 1.45;
}

.vision-image-warning__content strong {
  font-size: 14px;
  font-weight: 700;
}

.vision-detection-alert {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff7ed;
  border-bottom: 1px solid #fcd9b6;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.3;
}

.vision-save-required-hint {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin: var(--vision-space-4) 0 calc(-1 * var(--vision-space-4)) 0;
  color: #92400e;
  font-size: 12.5px;
  font-weight: 600;
}

.vision-step__actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: var(--vision-space-5);
}

.vision-step__actions--split {
  justify-content: space-between;
}

.vision-btn-primary {
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.1px !important;
  border-radius: var(--vision-radius-md) !important;
  height: 46px !important;
  padding: 0 22px !important;
  box-shadow: var(--vision-shadow-primary-soft) !important;
}

.vision-btn-primary.v-btn--disabled {
  box-shadow: none !important;
  background-color: var(--vision-surface-3) !important;
  color: var(--vision-ink-5) !important;
}

.vision-btn-outline {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: var(--vision-radius-md) !important;
  height: 46px !important;
  padding: 0 20px !important;
}

.vision-btn-text {
  text-transform: none !important;
  font-weight: 600 !important;
}

/* ===== Loading ===== */
.vision-loading {
  display: flex;
  align-items: flex-start;
  gap: var(--vision-space-4);
  margin-top: var(--vision-space-5);
  padding: var(--vision-space-4);
  background: var(--vision-primary-softer);
  border: 1px solid rgba(70, 118, 212, 0.16);
  border-radius: var(--vision-radius-md);
}

.vision-loading__spinner {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid rgba(70, 118, 212, 0.25);
  border-top-color: var(--vision-primary);
  animation: vision-spin 0.9s linear infinite;
  margin-top: 2px;
}

@keyframes vision-spin {
  to { transform: rotate(360deg); }
}

.vision-loading__title {
  margin: 0;
  font-weight: 700;
  color: var(--vision-ink-2);
  font-size: 14px;
}

.vision-loading__subtitle {
  margin: 4px 0 0;
  color: var(--vision-ink-5);
  font-size: 13px;
}

.vision-loading__steps {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vision-loading__steps li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--vision-ink-5);
  transition: color var(--vision-anim-base) var(--vision-ease);
}

.vision-loading__steps li.is-active {
  color: var(--vision-primary-dark);
  font-weight: 600;
}

.vision-loading__steps li.is-done {
  color: var(--vision-ink-4);
}

/* ===== Result hero ===== */
.vision-result-hero {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--vision-space-4);
  padding: var(--vision-space-5) var(--vision-space-5) var(--vision-space-5) calc(var(--vision-space-5) + 8px);
  border-radius: var(--vision-radius-lg);
  border: 1px solid var(--vision-border);
  background: linear-gradient(135deg, #ffffff 0%, color-mix(in srgb, var(--result-color) 8%, white) 100%);
  overflow: hidden;
  margin-bottom: var(--vision-space-4);
  animation: vision-pop-in var(--vision-anim-slow) var(--vision-ease);
}

.vision-result-hero__bar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  background: var(--result-color);
}

.vision-result-hero__icon {
  flex: 0 0 auto;
  width: 72px;
  height: 72px;
  border-radius: var(--vision-radius-md);
  background: color-mix(in srgb, var(--result-color) 15%, white);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.vision-result-hero__body {
  flex: 1 1 auto;
  min-width: 0;
}

.vision-result-hero__body h2 {
  margin: 4px 0;
  font-size: 30px;
  letter-spacing: -0.4px;
  font-weight: 800;
  color: var(--result-color);
  text-transform: uppercase;
  line-height: 1.1;
}

.vision-result-hero__meta {
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  color: var(--vision-ink-4);
  font-size: 13px;
}

.vision-result-hero__meta span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.vision-result-image {
  border-radius: var(--vision-radius-md);
  overflow: hidden;
  margin-bottom: var(--vision-space-4);
  box-shadow: var(--vision-shadow-1);
}

.vision-result-charts {
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-md) !important;
  overflow: hidden;
}

.vision-result-charts + .vision-result-charts {
  margin-top: var(--vision-space-3);
}

.vision-result-charts ::v-deep .v-expansion-panel-header {
  font-weight: 600;
  color: var(--vision-ink-2);
}

/* ===== Empty state ===== */
.vision-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: var(--vision-space-7) var(--vision-space-4);
}

.vision-empty h3 {
  margin: 0;
  color: var(--vision-ink-2);
  font-size: 18px;
  font-weight: 700;
}

.vision-empty p {
  margin: 0;
  color: var(--vision-ink-5);
  font-size: 14px;
}

/* ===== Save form ===== */
.vision-save-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: var(--vision-space-5);
}

.vision-save-form {
  display: flex;
  flex-direction: column;
  gap: var(--vision-space-4);
}

.vision-fieldset {
  border: 0;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--vision-space-3);
}

.vision-fieldset legend {
  margin-bottom: 4px;
  padding: 0;
}

.vision-save-preview {
  position: relative;
}

.vision-save-preview__sticky {
  position: sticky;
  top: 12px;
  display: flex;
  flex-direction: column;
  gap: var(--vision-space-3);
  padding: var(--vision-space-4);
  background: var(--vision-surface-2);
  border: 1px solid var(--vision-border);
  border-radius: var(--vision-radius-md);
}

.vision-save-preview__result {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  align-self: flex-start;
  padding: 6px 12px;
  border-radius: var(--vision-radius-pill);
  background: color-mix(in srgb, var(--result-color) 12%, white);
  border: 1px solid color-mix(in srgb, var(--result-color) 30%, transparent);
  color: var(--result-color);
  font-weight: 700;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.vision-save-preview__image {
  border-radius: var(--vision-radius-md);
  overflow: hidden;
  background: var(--vision-surface);
  border: 1px solid var(--vision-border);
}

.vision-save-preview__image img {
  display: block;
  width: 100%;
  max-height: 220px;
  object-fit: cover;
}

.vision-save-preview__meta {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.vision-save-preview__meta li {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--vision-ink-4);
}

/* ===== Success ===== */
.vision-step--success {
  padding: var(--vision-space-6) 0;
}

.vision-success {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: var(--vision-space-5);
}

.vision-success__check {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--vision-success) 0%, #14b061 100%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 14px 28px rgba(25, 135, 84, 0.28);
  margin-bottom: var(--vision-space-3);
  animation: vision-pop-in 420ms var(--vision-ease);
}

.vision-success h2 {
  margin: 0;
  font-size: 24px;
  color: var(--vision-ink-1);
  font-weight: 800;
}

.vision-success p {
  margin: 0;
  color: var(--vision-ink-4);
  font-size: 15px;
  max-width: 420px;
}

.vision-success__actions {
  display: flex;
  gap: 12px;
  margin-top: var(--vision-space-4);
  flex-wrap: wrap;
  justify-content: center;
}

/* ===== Transitions ===== */
.vision-step-enter-active,
.vision-step-leave-active {
  transition: opacity var(--vision-anim-base) var(--vision-ease),
              transform var(--vision-anim-base) var(--vision-ease);
}

.vision-step-enter {
  opacity: 0;
  transform: translateY(8px);
}

.vision-step-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.vision-fade-enter-active,
.vision-fade-leave-active {
  transition: opacity var(--vision-anim-base) var(--vision-ease),
              transform var(--vision-anim-base) var(--vision-ease);
}

.vision-fade-enter {
  opacity: 0;
  transform: translateY(-4px);
}

.vision-fade-leave-to {
  opacity: 0;
}

/* ===== Responsive ===== */
@media (max-width: 960px) {
  .vision-stepper {
    display: none;
  }

  .vision-save-grid {
    grid-template-columns: 1fr;
  }

  .vision-save-preview__sticky {
    position: static;
  }
}

@media (max-width: 760px) {
  .vision-step-card {
    padding: var(--vision-space-4);
  }

  .vision-hero {
    padding: var(--vision-space-4);
  }

  .vision-hero__text h1 {
    font-size: 24px;
  }

  .vision-result-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .vision-result-hero__body h2 {
    font-size: 24px;
  }

  .vision-step__actions--split {
    flex-direction: column-reverse;
    align-items: stretch;
  }

  .vision-step__actions--split .v-btn {
    width: 100%;
  }
}
</style>
